/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Immutable from 'immutable';
import { Form, FormGroup } from 'reactstrap';
import { Field, reduxForm, change } from 'redux-form';
import { tzNormalizeDate } from '../../../../utils/date';
import { INT_DATE_FORMAT } from '../../../../config/locale';
import FormItem from '../../../common/forms/FormItem';
import { isRequired, validateNumber } from '../../../../utils/validators';
import Loader from '../../../common/Loader';
import FormFooter from '../../../common/forms/FormFooter';
import {
  clearItinerarySupplierFoodReport,
  getItinerarySupplierFoodReportInvoice,
} from '../../../../actions/food/ItinerarySupplierFood';
import SelectionableTable from '../../../common/forms/table/SelectionableTable';
import ItinerarySupplierFoodReportForm from '../itinerary-supplier-food-report/ItinerarySupplierFoodReportForm';
import { DEFAULT_QUERY_GET_ALL } from '../../../../config/queries';
import DatePicker from '../../../common/forms/input/DatePicker';
import { DEFAULT_PAGE_SIZE } from '../../../../config/constants';
import { filterMethodCaseInsensitive } from '../../../../utils/filter-methods';
import { ITINERARY_FOOD_INVOICE_DETAIL_COLUMNS } from '../../../../config/columns';
import Alert from '../../../common/informative/Alert';
import TextInput from '../../../common/forms/input/TextInput';

const FORM_NAME = 'ItineraryFoodInvoiceForm';

const ItineraryFoodInvoiceForm = ({
  handleSubmit,
  onSubmit,
  dispatchClearItinerarySupplierFoodReport,
  dispatchGetItinerarySupplierFoodReportInvoice,
  dispatchChange,
  itinerarySupplierFoods,
  loading,
  loadingReport,
  edit,
  initialSearchValues,
  preSelectedIds,
}) => {
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [periodStartDate, setPeriodStartDate] = useState(null);
  const [periodEndDate, setPeriodEndDate] = useState(null);
  const [searched, setSearched] = useState(false);

  const initialSearchExecuted = useRef(false);
  const lastCalculatedAmount = useRef(null);

  const itinerarySupplierFoodList = useMemo(() => {
    if (Immutable.List.isList(itinerarySupplierFoods)) {
      return itinerarySupplierFoods.toJS();
    }

    return itinerarySupplierFoods || [];
  }, [itinerarySupplierFoods]);

  const normalizedPreSelectedIds = useMemo(
    () => preSelectedIds || [],
    [preSelectedIds],
  );

  const hasData =
    itinerarySupplierFoodList && itinerarySupplierFoodList.length > 0;

  const showInitialMessage = !searched && !loadingReport;
  const showEmptyAlert = searched && !loadingReport && !hasData;

  useEffect(
    () => () => {
      dispatchClearItinerarySupplierFoodReport();
      dispatchChange(FORM_NAME, 'itinerarySupplierFoodDetailList', null);
      dispatchChange(FORM_NAME, 'amount', null);
    },
    [dispatchClearItinerarySupplierFoodReport, dispatchChange],
  );

  const onSearch = (values) => {
    dispatchClearItinerarySupplierFoodReport();
    dispatchChange(FORM_NAME, 'itinerarySupplierFoodDetailList', null);
    dispatchChange(FORM_NAME, 'amount', null);
    lastCalculatedAmount.current = null;
    dispatchGetItinerarySupplierFoodReportInvoice(values);
  };

  useEffect(() => {
    if (!edit || !initialSearchValues || initialSearchExecuted.current) return;

    initialSearchExecuted.current = true;

    const supplier = initialSearchValues.supplierId || null;
    const from = initialSearchValues.fromDateString;
    const to = initialSearchValues.toDateString;

    setSelectedSupplier(supplier);
    setPeriodStartDate(from);
    setPeriodEndDate(to);
    setSearched(true);

    dispatchClearItinerarySupplierFoodReport();
    dispatchChange(FORM_NAME, 'itinerarySupplierFoodDetailList', null);
    dispatchChange(FORM_NAME, 'amount', null);
    lastCalculatedAmount.current = null;

    dispatchGetItinerarySupplierFoodReportInvoice({
      supplierId: supplier ? supplier.value : null,
      from,
      to,
    });
  }, [
    edit,
    initialSearchValues,
    dispatchClearItinerarySupplierFoodReport,
    dispatchGetItinerarySupplierFoodReportInvoice,
    dispatchChange,
  ]);

  useEffect(() => {
    if (!edit || !normalizedPreSelectedIds.length) return;
    if (!itinerarySupplierFoodList || !itinerarySupplierFoodList.length) return;

    const totalAmount = normalizedPreSelectedIds.reduce((total, id) => {
      const item = itinerarySupplierFoodList.find(
        (detail) => Number(detail.id) === Number(id),
      );

      return total + Number(item && item.amount ? item.amount : 0);
    }, 0);

    const amount = totalAmount.toFixed(2);

    if (lastCalculatedAmount.current === amount) return;

    lastCalculatedAmount.current = amount;
    dispatchChange(FORM_NAME, 'amount', amount);
  }, [
    edit,
    normalizedPreSelectedIds,
    itinerarySupplierFoodList,
    dispatchChange,
  ]);

  const onSubmitSearch = (formValues) => {
    const from = tzNormalizeDate({
      date: formValues.fromDateString,
      format: INT_DATE_FORMAT,
    });

    const to = tzNormalizeDate({
      date: formValues.toDateString,
      format: INT_DATE_FORMAT,
    });

    setSelectedSupplier(formValues.supplierId || null);
    setPeriodStartDate(from);
    setPeriodEndDate(to);
    setSearched(true);

    const newSearchValues = {
      supplierId: formValues.supplierId ? formValues.supplierId.value : null,
      from,
      to,
      ...DEFAULT_QUERY_GET_ALL,
    };

    onSearch(newSearchValues);
  };

  const onSelectionChange = ({ selectedIds }) => {
    const totalAmount = (selectedIds || []).reduce((total, id) => {
      const item = itinerarySupplierFoodList.find(
        (detail) => Number(detail.id) === Number(id),
      );

      return total + Number(item && item.amount ? item.amount : 0);
    }, 0);

    const amount = totalAmount.toFixed(2);

    lastCalculatedAmount.current = amount;
    dispatchChange(FORM_NAME, 'amount', amount);
  };

  const onHandleSubmitLiquidation = (formValues) => {
    const payload = {
      ...formValues,
      supplierId: selectedSupplier ? selectedSupplier.value : null,
      periodStartDate,
      periodEndDate,
      itinerarySupplierFoodDetailList:
        formValues.itinerarySupplierFoodDetailList || [],
      amount: formValues.amount ? Number(formValues.amount) : 0,
    };

    onSubmit(payload, selectedSupplier);
  };

  const searchInitialValues = initialSearchValues || {
    fromDateString: tzNormalizeDate(),
    toDateString: tzNormalizeDate(),
  };

  return (
    <>
      <h3>Búsqueda de Viajes por Proveedor</h3>

      <ItinerarySupplierFoodReportForm
        onSubmit={onSubmitSearch}
        initialValues={searchInitialValues}
      />

      <hr />

      <Form className="mt-3" onSubmit={handleSubmit(onHandleSubmitLiquidation)}>
        {loadingReport && <Loader />}

        {showInitialMessage && (
          <Alert
            type="info"
            message="Seleccione un proveedor y rango de fechas para buscar viajes."
          />
        )}

        {showEmptyAlert && (
          <Alert
            type="warning"
            message="No se encontraron viajes para el proveedor y rango de fechas seleccionado."
          />
        )}

        {hasData && (
          <SelectionableTable
            manual={false}
            columns={ITINERARY_FOOD_INVOICE_DETAIL_COLUMNS}
            data={itinerarySupplierFoodList}
            loading={loadingReport}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            filterable
            defaultFilterMethod={filterMethodCaseInsensitive}
            returnOnlySelectedItems
            keyField="id"
            form={FORM_NAME}
            name="itinerarySupplierFoodDetailList"
            preSelectedIds={normalizedPreSelectedIds}
            onSelectionChange={onSelectionChange}
          />
        )}

        <hr />

        {hasData && (
          <>
            <FormGroup row>
              <FormItem label="Fecha de Emisión" required>
                <Field
                  name="issueDate"
                  component={DatePicker}
                  validate={[isRequired]}
                />
              </FormItem>
            </FormGroup>

            <FormGroup row>
              <FormItem label="Fecha de Vencimiento">
                <Field name="expirationDate" component={DatePicker} />
              </FormItem>
            </FormGroup>

            <FormGroup row>
              <FormItem label="Fecha de Entrega a Contabilidad" required>
                <Field
                  name="accountingDeliveredDate"
                  component={DatePicker}
                  validate={[isRequired]}
                />
              </FormItem>
            </FormGroup>

            <FormGroup row>
              <FormItem label="N° Resumen" required>
                <Field
                  name="summaryNumber"
                  component={TextInput}
                  validate={[isRequired]}
                />
              </FormItem>
            </FormGroup>

            <FormGroup row>
              <FormItem label="Serie de Documento" required>
                <Field
                  name="documentSeries"
                  component={TextInput}
                  validate={[isRequired]}
                />
              </FormItem>
            </FormGroup>

            <FormGroup row>
              <FormItem label="Correlativo de Documento" required>
                <Field
                  name="documentCode"
                  component={TextInput}
                  validate={[isRequired, validateNumber]}
                />
              </FormItem>
            </FormGroup>
            <FormGroup row>
              <FormItem label="Descripción" required>
                <Field
                  name="description"
                  type="textarea"
                  component={TextInput}
                  validate={[isRequired]}
                />
              </FormItem>
            </FormGroup>

            <FormGroup row>
              <FormItem label="Monto Total" required>
                <Field
                  name="amount"
                  component={TextInput}
                  validate={[isRequired, validateNumber]}
                  disabled
                />
              </FormItem>
            </FormGroup>

            <FormFooter
              saveButtonDisabled={loading}
              saveButtonIcon={loading ? 'fa fa-spinner fa-spin' : 'fa fa-save'}
            />
          </>
        )}
      </Form>
    </>
  );
};

ItineraryFoodInvoiceForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dispatchClearItinerarySupplierFoodReport: PropTypes.func.isRequired,
  dispatchGetItinerarySupplierFoodReportInvoice: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loadingReport: PropTypes.bool,
  itinerarySupplierFoods: PropTypes.oneOfType([
    PropTypes.instanceOf(Immutable.List),
    PropTypes.arrayOf(PropTypes.shape({})),
  ]),
  edit: PropTypes.bool,
  initialSearchValues: PropTypes.shape({}),
  preSelectedIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ),
};

ItineraryFoodInvoiceForm.defaultProps = {
  loading: false,
  loadingReport: false,
  edit: false,
  initialSearchValues: null,
  preSelectedIds: [],
  itinerarySupplierFoods: Immutable.List(),
};

const mapDispatchToProps = {
  dispatchClearItinerarySupplierFoodReport: clearItinerarySupplierFoodReport,
  dispatchGetItinerarySupplierFoodReportInvoice:
    getItinerarySupplierFoodReportInvoice,
  dispatchChange: change,
};

const mapStateToProps = ({ FoodUnit }) => ({
  loading: !FoodUnit.ItineraryFoodInvoice.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  loadingReport: FoodUnit.ItinerarySupplierFood.getIn(['report', 'loading']),
  itinerarySupplierFoods: FoodUnit.ItinerarySupplierFood.getIn(
    ['report', 'content', 'content'],
    Immutable.List(),
  ),
});

const formComponent = reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
})(ItineraryFoodInvoiceForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
